import '@blocknote/core/fonts/inter.css'
import { BlockNoteView } from '@blocknote/mantine'
import '@blocknote/mantine/style.css'
import { MantineProvider } from '@mantine/core'
import { SideMenuController, useCreateBlockNote } from '@blocknote/react'
import type { PartialBlock } from '@blocknote/core'
import React, { useCallback, useEffect, useRef } from 'react'
import type { WorkspaceDocument } from '../../Models/Workspace'
import WorkspaceSideMenu from './WorkspaceSideMenu'
import styles from './WorkspaceEditor.module.css'

type WorkspaceEditorProps = {
  document: WorkspaceDocument
  onSave: (patch: { title?: string; content?: string }) => void
}

const DEFAULT_BLOCKS = [{ type: 'paragraph', content: '' }] as PartialBlock[]

function readInitialContent(json: string): PartialBlock[] {
  try {
    const parsed = JSON.parse(json) as unknown
    if (!Array.isArray(parsed)) return DEFAULT_BLOCKS
    if (parsed.length === 0) return DEFAULT_BLOCKS
    return parsed as PartialBlock[]
  } catch {
    return DEFAULT_BLOCKS
  }
}

const WorkspaceEditor = ({ document, onSave }: WorkspaceEditorProps) => {
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const titleRef = useRef<HTMLInputElement>(null)

  const initialContent = readInitialContent(document.content)

  const editor = useCreateBlockNote({ initialContent }, [document.id])

  const scheduleSave = useCallback(
    (patch: { title?: string; content?: string }) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
        onSave(patch)
      }, 1000)
    },
    [onSave]
  )

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  const handleTitleBlur = () => {
    const newTitle = titleRef.current?.value.trim() || 'Untitled'
    if (newTitle !== document.title) {
      scheduleSave({ title: newTitle })
    }
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      titleRef.current?.blur()
    }
  }

  return (
    <div className={styles.editorContainer}>
      <input
        ref={titleRef}
        className={styles.titleInput}
        defaultValue={document.title}
        placeholder="Untitled"
        onBlur={handleTitleBlur}
        onKeyDown={handleTitleKeyDown}
        aria-label="Document title"
      />
      <div className={styles.blockEditor}>
        <MantineProvider>
          <BlockNoteView
            editor={editor}
            sideMenu={false}
            onChange={() => {
              const blocks = editor.document
              scheduleSave({ content: JSON.stringify(blocks) })
            }}
            theme="dark"
          >
            <SideMenuController sideMenu={WorkspaceSideMenu} />
          </BlockNoteView>
        </MantineProvider>
      </div>
    </div>
  )
}

export default WorkspaceEditor
